import { useState } from "react";
import dynamicExportService from "@/services/dynamicExporter/dynamicExportService";
import { triggerDownload, ObjectToFormData } from "@/utils";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Loader2, FileText, FileX, FileIcon, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { useAbility } from "@/providers/";
import { pendingApprovalMessage } from "@/utils/exportResponse";

const Exporter = ({ label = 'Download Report', module, columns, filters, tableRef, needColToExport = false, hidePaymentSummary = false }) => {
    const [loading, setLoading] = useState(false);
    const { ability } = useAbility();

    const handleExport = async (fileType) => {
        setLoading(true);

        try {
            const formData = new FormData();

            // Read live visibility from DataTables
            const dt = tableRef?.current?.dt();
            let visibleCols = [];

            if (dt) {
                const visibility = dt.columns().visible(); // [true, false, true...]

                visibleCols = columns
                    .filter((col, idx) => visibility[idx] && col.data)
                    .map(col => col.data);

                if (visibleCols.length === 0) {
                    toast.warning('Please select at least one column to download.');
                    return
                }
            } else {
                // fallback: all columns
                visibleCols = columns
                    .map(col => col.data)
                    .filter(Boolean);
            }

            visibleCols = visibleCols.map(col => {
                if (col === 'settlements[0].amount') {
                    return 'settlement';
                }
                if (col === 'invoices') {
                    // Send 'invoice_total' for export, which is just the count of invoices
                    return 'total_invoices';
                }
                if (col === 'invoices.total') {
                    // Send 'invoice_total' for export, which is just the count of invoices
                    return 'invoices_total';
                }
                return col;
            });

            // Append visible columns[] only
            visibleCols.forEach(c => {
                formData.append("columns[]", c);
            });

            // Append filters
            const filtersFD = ObjectToFormData(filters);
            for (const [key, value] of filtersFD.entries()) {
                formData.append(key, value);
            }

            // module + file type
            formData.append("model", module);
            formData.append("download", fileType);
            formData.append("ar_report", hidePaymentSummary);

            // Send export request
            const blob = await dynamicExportService.exportTableReports(formData);

            // With "Exports require approval" on, the server answers with a
            // pending request instead of the file (OL-154).
            const pending = await pendingApprovalMessage(blob);
            if (pending) {
                toast.info("Export sent for approval", { description: pending });
                return;
            }

            triggerDownload(blob, `${module}.${fileType}`);

            toast.success("Report downloaded!");
        } catch (err) {
            toast.error("Failed to export report.");
        } finally {
            setLoading(false);
        }
    };


    // Downloads need the Export Data permission (OL-154).
    if (!ability.can('export', 'data')) return null;

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    className="btn btn-sm btn-light btn-outline text-xs flex items-center gap-2"
                    disabled={loading}
                >
                    {loading ? (
                        <>
                            <Loader2 className="h-3 w-3 animate-spin" />
                            downloading...
                        </>
                    ) : (
                        <>
                            {label} <ChevronDown className="size-4" />
                        </>
                    )}
                </button>
            </DropdownMenuTrigger>

            <DropdownMenuContent className="w-40 p-1">
                <DropdownMenuItem onClick={() => handleExport("pdf")} className="text-xs cursor-pointer">
                    <FileText className="size-4" /> PDF
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport("xlsx")} className="text-xs cursor-pointer">
                    <FileX className="size-4" /> Excel
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

export { Exporter };